import { useState } from 'react';
import {
  CaretDown, CaretRight, ArrowSquareOut, Info, MapPin, SealCheck, Circle,
} from '@phosphor-icons/react';

// Beta-3.1 · 回答の表示は1か所だけが担当します。
// アシスタント画面（/documents/assistant）とビューア内パネルは同じこの部品を使い、
// 言語・一致の強さ・確認状態・警告・階層が入口ごとに変わらないようにします。
const MATCH_STYLE = {
  strong: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  possible: 'bg-amber-50 text-amber-800 ring-amber-200',
  weak: 'bg-zinc-50 text-zinc-600 ring-zinc-200',
};

const STATE_ICON = {
  confirmed: SealCheck,
  unconfirmed: Circle,
  ocr: Circle,
};

export const Pill = ({ className = '', children, testid }) => (
  <span data-testid={testid}
    className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] ring-1 ${className}`}>
    {children}
  </span>
);

export function SourceCard({ match, index, labels, onOpenDoc }) {
  const [rawOpen, setRawOpen] = useState(false);
  const top = match.citations?.[0];
  const StateIcon = STATE_ICON[match.state] || Circle;
  const href = match.module === 'document' && match.document_id
    ? `/documents?open=${match.document_id}` : (match.url || '#');
  return (
    <div className="rounded-xl bg-white ring-1 ring-zinc-200 p-4 transition-shadow duration-200 hover:shadow-sm"
      data-testid={`assistant-source-card-${index}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-base font-semibold text-zinc-900 break-words"
            title={match.primary_identifier}
            data-testid={`assistant-source-identity-${index}`}>
            {match.primary_identifier}
          </p>
          <p className="mt-0.5 text-xs text-zinc-400 break-words">
            {match.document_number && (
              <span data-testid={`assistant-source-docno-${index}`}>
                {match.document_number_label} {match.document_number}
              </span>
            )}
            {match.document_type_label && <span> · {match.document_type_label}</span>}
            {match.folder_path && <span> · {match.folder_path}</span>}
          </p>
        </div>
        <Pill className={MATCH_STYLE[match.match_class] || MATCH_STYLE.weak}
          testid={`assistant-match-class-${index}`}>
          {match.match_class_label}
        </Pill>
      </div>

      <p className="mt-2 text-sm text-zinc-600">{match.why_matched}</p>

      {top && (
        <div className="mt-2 space-y-1">
          {top.field_label && top.value && (
            <p className="text-sm text-zinc-900">
              <span className="text-zinc-500">{top.field_label}: </span>
              <span className="font-medium" data-testid={`assistant-source-value-${index}`}>{top.value}</span>
            </p>
          )}
          <p className="flex items-center gap-1.5 text-sm text-teal-800">
            <MapPin size={13} className="shrink-0" />
            <span data-testid={`assistant-citation-${index}-0`}>{top.citation}</span>
          </p>
          <p className="flex items-center gap-1.5 text-xs text-zinc-500">
            <StateIcon size={12} weight={match.state === 'confirmed' ? 'fill' : 'regular'}
              className={match.state === 'confirmed' ? 'text-emerald-600' : 'text-amber-500'} />
            <span data-testid={`assistant-source-state-${index}`}>{match.state_label}</span>
          </p>
        </div>
      )}

      {match.citations?.length > 1 && (
        <ul className="mt-2 space-y-1">
          {match.citations.slice(1).map((c, ci) => (
            <li key={ci} className="flex items-center gap-1.5 text-xs text-teal-800">
              <MapPin size={11} className="shrink-0" />
              <span data-testid={`assistant-citation-${index}-${ci + 1}`}>{c.citation}</span>
              {c.value && <span className="text-zinc-500">— {c.value}</span>}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-4">
        <a href={href} target="_blank" rel="noreferrer"
          onClick={(e) => {
            if (onOpenDoc && match.document_id) { e.preventDefault(); onOpenDoc(match.document_id); }
          }}
          data-testid={`assistant-open-source-${index}`}
          className="inline-flex items-center gap-1.5 min-h-[32px] text-sm text-teal-700 hover:text-teal-900 hover:underline">
          <ArrowSquareOut size={14} /> {match.open_label || labels?.open_original}
        </a>
        {top?.snippet && (
          <button type="button" onClick={() => setRawOpen((v) => !v)}
            data-testid={`assistant-raw-toggle-${index}`}
            aria-expanded={rawOpen}
            className="inline-flex items-center gap-1 min-h-[32px] text-xs text-zinc-500 hover:text-zinc-800">
            {rawOpen ? <CaretDown size={11} /> : <CaretRight size={11} />}
            {rawOpen ? (labels?.hide_extracted || '') : (match.show_extracted_label || '')}
          </button>
        )}
      </div>

      {rawOpen && top?.snippet && (
        <p className="mt-2 rounded-lg bg-zinc-50 ring-1 ring-zinc-200/70 px-3 py-2 text-xs leading-relaxed text-zinc-500 break-words"
          data-testid={`assistant-raw-text-${index}`}>
          {top.snippet}
        </p>
      )}
    </div>
  );
}

export default function AssistantAnswer({ result, compact = false, onOpenDoc }) {
  const [showWhy, setShowWhy] = useState(false);
  const [showWeak, setShowWeak] = useState(false);
  if (!result) return null;
  const labels = result.labels || {};
  const prose = result.narrative || result.narrative_ja || result.answer || result.answer_ja || '';
  const groups = result.groups || { strong: [], possible: [], weak: [] };
  const groupLabels = result.group_labels || {};
  const note = result.verification_note;
  const strong = groups.strong || [];
  const possible = groups.possible || [];
  const weak = groups.weak || [];
  const count = (n) => (result.language === 'ja' ? `（${n}）` : ` (${n})`);
  let i = 0;

  return (
    <div className={compact ? 'space-y-3' : 'space-y-5'} data-testid="assistant-answer">
      {/* 1 · 結論 */}
      <div className="rounded-2xl bg-white ring-1 ring-zinc-200 p-4">
        <p className="max-w-[72ch] text-[15px] leading-relaxed text-zinc-900 whitespace-pre-line"
          data-testid="assistant-answer-text">
          {prose}
        </p>
      </div>

      {/* 2 · 最も一致する書類 → 3 · 可能性のある一致 */}
      {[['strong', strong], ['possible', possible]].map(([key, list]) => (
        list.length > 0 && (
          <section key={key} data-testid={`assistant-group-${key}`}>
            <h3 className="mb-2 text-sm font-semibold text-zinc-500">
              {groupLabels[key]}{list.length > 1 ? count(list.length) : ''}
            </h3>
            <div className="space-y-2">
              {list.map((m) => {
                const idx = i; i += 1;
                return <SourceCard key={m.document_id || idx} match={m} index={idx}
                  labels={labels} onOpenDoc={onOpenDoc} />;
              })}
            </div>
          </section>
        )
      ))}

      {/* 4 · 確認について — 1言語・1ブロック */}
      {note && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3"
          data-testid="assistant-verification-note">
          <p className="text-sm font-medium text-amber-900">{note.title}</p>
          <p className="mt-0.5 max-w-[72ch] text-sm text-amber-800">{note.text}</p>
        </div>
      )}

      {/* 5 · 参考程度の一致 — 折りたたみ */}
      {weak.length > 0 && (
        <section data-testid="assistant-group-weak">
          <button type="button" onClick={() => setShowWeak((v) => !v)}
            data-testid="assistant-weak-toggle" aria-expanded={showWeak}
            className="inline-flex items-center gap-1.5 min-h-[32px] text-sm text-zinc-500 hover:text-zinc-800">
            {showWeak ? <CaretDown size={12} /> : <CaretRight size={12} />}
            {groupLabels.weak}{count(weak.length)}
          </button>
          {showWeak && (
            <div className="mt-2 space-y-2">
              {weak.map((m) => {
                const idx = i; i += 1;
                return <SourceCard key={m.document_id || idx} match={m} index={idx}
                  labels={labels} onOpenDoc={onOpenDoc} />;
              })}
            </div>
          )}
        </section>
      )}

      {/* 6 · なぜこの回答になったか（根拠の強さはここ） */}
      {result.explain && (
        <div className="rounded-2xl bg-white ring-1 ring-zinc-200">
          <button type="button" onClick={() => setShowWhy((v) => !v)}
            data-testid="assistant-explain-toggle" aria-expanded={showWhy}
            className="w-full px-4 py-3 flex items-center gap-2 text-sm text-zinc-700">
            <Info size={15} className="text-zinc-400" />
            {labels.why_heading || 'なぜこの回答になったか'}
            {result.confidence && (
              <span className="ml-2 text-xs text-zinc-400" data-testid="assistant-confidence">
                {result.confidence.strength_label}: {result.confidence.label}
              </span>
            )}
            <span className="ml-auto text-zinc-400">
              {showWhy ? <CaretDown size={13} /> : <CaretRight size={13} />}
            </span>
          </button>
          {showWhy && (
            <div className="px-4 pb-4 space-y-3 text-xs text-zinc-600"
              data-testid="assistant-explain-body">
              <p className="max-w-[72ch] leading-relaxed">
                {result.explain.why || result.explain.why_ja}
              </p>
              {result.normalized?.search_terms?.length > 0 && (
                <div className="space-y-0.5 text-zinc-500">
                  <p data-testid="assistant-searched-terms">
                    {result.normalized.searched_for_label}: {result.normalized.search_terms.join(' · ')}
                  </p>
                  {result.normalized.intent_label && (
                    <p>{labels.intent_label_prefix}: {result.normalized.intent_label}</p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[[labels.explain_scope, result.scope_target || result.scope_target_ja],
                  [labels.explain_considered, result.explain.documents_considered],
                  [labels.explain_matched, result.explain.documents_matched
                    ?? (result.explain.documents_contributed?.length || 0)],
                  [labels.explain_excluded, (result.explain.excluded || [])
                    .reduce((a, e) => a + (e.count || 1), 0)]]
                  .map(([label, v], k) => (
                    <div key={k} className="rounded-lg bg-zinc-50 ring-1 ring-zinc-200/70 px-3 py-2">
                      <div className="text-zinc-400">{label}</div>
                      <div className="text-sm font-semibold text-zinc-900 break-words">{v}</div>
                    </div>
                  ))}
              </div>
              {result.explain.excluded?.length > 0 && (
                <ul className="space-y-0.5" data-testid="assistant-excluded">
                  {result.explain.excluded.map((e, k) => (
                    <li key={k}>
                      {result.language === 'ja' ? '・' : '— '}
                      {e.reason || e.reason_ja}
                      {e.count > 1 ? count(e.count) : ''}
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-zinc-400">{labels.explain_disclaimer}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
